import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, defer, map, of, retry, shareReplay, switchMap, tap, timer } from 'rxjs';

import { ConfigurationService, InfraPreset, Logger, SKIP_JAP_HEADER_CONTEXT } from '@mobi/rwc-utils-common-ng-jslib';

import { CipError } from '../../../auth/internal/access-token-error-handler.service';
import { TopologyIntegrationService } from '../topology/topology-integration.service';

import { ApiDiscoveryEntry } from './api-discovery.types';
import { LOCAL_STORAGE } from './local-storage';

const ONE_DAY = 1000 * 60 * 60 * 24;
const API_DISCOVERY_STORAGE_KEY = 'rwc.config.apiDiscovery.v2';
const INFRA_PRESETS_WITH_SERVICE_DISCOVERY: InfraPreset[] = ['b2e', 'b2c', 'b2b', 'b2eadmin'];

@Injectable({ providedIn: 'root' })
export class ApiDiscoveryIntegrationService {
    #configurationService = inject(ConfigurationService);
    #http = inject(HttpClient);
    #localStorage = inject(LOCAL_STORAGE);
    #logger = inject(Logger).createCustomLogger('Service discovery');
    #topologyService = inject(TopologyIntegrationService);

    #currentServiceDiscoveryRequest$ = this.#getServiceDiscoveryEntries$().pipe(
        tap(serviceDefinitions => this.#storeServiceDefinitions(serviceDefinitions)),
        shareReplay(1)
    );

    readonly apiDiscoveryResult$ = defer(() => {
        // don't use cache in local dev mode because when working with local backends the data will not be updated
        const serviceDefinitions = this.#configurationService.isStage('local-dev')
            ? undefined
            : this.#loadCachedServiceDiscoveryEntries();
        return serviceDefinitions ? of(serviceDefinitions) : this.#currentServiceDiscoveryRequest$;
    });

    #storeServiceDefinitions(serviceDefinitions: ApiDiscoveryEntry[]): void {
        if (serviceDefinitions.length > 0) {
            return this.#localStorage.setItem(
                API_DISCOVERY_STORAGE_KEY,
                JSON.stringify({ entries: serviceDefinitions, timestamp: Date.now() } as StoredServiceDefinitions)
            );
        }
    }

    #loadCachedServiceDiscoveryEntries(): ApiDiscoveryEntry[] | undefined {
        const storedServiceDefinitions = this.#localStorage.getItem(API_DISCOVERY_STORAGE_KEY);
        if (!storedServiceDefinitions) {
            return;
        }
        const serviceDefinitions = JSON.parse(storedServiceDefinitions) as StoredServiceDefinitions;
        if (!serviceDefinitions.timestamp || serviceDefinitions.timestamp + ONE_DAY < Date.now()) {
            this.#logger.info('cache is expired, fetching new data.');
            return;
        }
        return serviceDefinitions.entries;
    }

    #getServiceDiscoveryEntries$(): Observable<ApiDiscoveryEntry[]> {
        const infraPreset = this.#configurationService.getSync('infraPreset');
        if (infraPreset && INFRA_PRESETS_WITH_SERVICE_DISCOVERY.includes(infraPreset)) {
            return this.#topologyService.topology$.pipe(
                map(topology => topology?.api_discovery_uri),
                switchMap(topologyServiceDiscoveryUri =>
                    this.#http.get<ApiDiscoveryEntry[]>(topologyServiceDiscoveryUri, SKIP_JAP_HEADER_CONTEXT)
                ),
                tap(entries => {
                    if (!entries?.length) {
                        throw new Error('No entries received from service discovery');
                    }
                }),
                retry({
                    count: 3,
                    delay: error => {
                        if (error instanceof CipError || (error instanceof HttpErrorResponse && error.status === 401)) {
                            // no retry for 401 / CipError
                            throw error;
                        }
                        return timer(300);
                    }
                })
            );
        }
        this.#logger.info(`not available for infra preset ${infraPreset}, return empty array.`);
        return of([] as ApiDiscoveryEntry[]);
    }
}

interface StoredServiceDefinitions {
    entries: ApiDiscoveryEntry[];
    timestamp: number;
}
